import {
  buildExpectedSku,
  createInitialState,
  findExactVariant,
  getAllowedOptions,
  getDutyCandidates,
  getDutyRows,
  getProfileGroups,
  getResolvedDuty,
  isConfigurationComplete,
  isVariantPurchasable,
  normalizeSelections,
} from './pfs-flow-switch-resolver.js?v=3';
import {
  formatDuty,
  formatSensing,
  getFlowSwitchProfile,
  getPipeLabel,
} from './pfs-flow-switch-profiles.js?v=4';

// Product pages above Shopify's 250-variant Liquid window resolve by option-value IDs below.

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class PfsFlowSwitchBuilder extends HTMLElementBase {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.controller = new AbortController();
    this.productScope = this.closest('[data-pfs-product]');
    this.fallback = this.productScope?.querySelector('[data-pfs-standard-purchase]');
    this.profile = getFlowSwitchProfile(this.dataset.productHandle);
    this.variants = this.readJson('[data-pfs-flow-switch-variants]', []);
    this.copy = this.readJson('[data-pfs-flow-switch-copy]', {});
    this.catalogOptionIds = new Map(Array.from(this.fallback?.querySelectorAll('[data-pfs-option]') || []).map((input) => [input.value, input.dataset.optionValueId]));
    this.remoteVariant = null;
    this.remoteKey = null;
    this.remoteStatus = 'idle';

    if (!this.profile) return;

    this.state = createInitialState(this.profile);
    this.cacheElements();
    this.configureProfile();
    this.bindEvents();
    this.hidden = false;
    if (this.fallback) this.fallback.hidden = true;
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.catalogController?.abort();
    delete this.dataset.ready;
  }

  readJson(selector, fallback) {
    const source = this.querySelector(selector)?.textContent;
    if (!source) return fallback;
    try {
      return JSON.parse(source);
    } catch {
      return fallback;
    }
  }

  cacheElements() {
    this.pipeChoices = this.querySelector('[data-pfs-flow-switch-pipes]');
    this.flowField = this.querySelector('[data-pfs-flow-switch-flow-field]');
    this.flowInput = this.querySelector('[data-pfs-flow-switch-flow]');
    this.bandsElement = this.querySelector('[data-pfs-flow-switch-bands]');
    this.dutyChoices = this.querySelector('[data-pfs-flow-switch-duty-choices]');
    this.dutyStatus = this.querySelector('[data-pfs-flow-switch-duty-status]');
    this.modelChoices = this.querySelector('[data-pfs-flow-switch-models]');
    this.modelGroups = this.querySelector('[data-pfs-flow-switch-model-groups]');
    this.electricalGroups = this.querySelector('[data-pfs-flow-switch-electrical-groups]');
    this.codeElement = this.querySelector('[data-pfs-flow-switch-code]');
    this.summaryDetails = this.querySelector('[data-pfs-flow-switch-details]');
    this.variantInput = this.querySelector('[data-pfs-flow-switch-variant-id]');
    this.addButton = this.querySelector('[data-pfs-flow-switch-add]');
    this.formStatus = this.querySelector('[data-pfs-flow-switch-form-status]');
    this.retryButton = this.querySelector('[data-pfs-flow-switch-retry]');
    this.form = this.variantInput?.form;
    this.quantityInput = this.form?.querySelector('input[name="quantity"]');
  }

  configureProfile() {
    this.setText('[data-pfs-flow-switch-eyebrow]', this.profile.eyebrow);
    this.setText('[data-pfs-flow-switch-heading]', this.profile.heading);
    const intro = this.querySelector('[data-pfs-flow-switch-intro]');
    if (intro) {
      intro.textContent = this.profile.intro || '';
      intro.hidden = !this.profile.intro;
    }
    this.setText('[data-pfs-flow-switch-family]', this.profile.family);
    this.setText('[data-pfs-flow-switch-selection-heading]', this.selectionHeading());
    this.setText('[data-pfs-flow-switch-selection-help]', this.selectionHelp());

    this.querySelectorAll('[data-pfs-flow-switch-rail]').forEach((item, index) => {
      const label = item.querySelector('strong');
      if (label) label.textContent = this.profile.progress[index] || '';
    });

    this.renderChoiceButtons(this.pipeChoices, this.profile.pipeOptions, 'pipe');
    this.renderChoiceButtons(this.modelChoices, this.profile.models, 'model');
    this.flowField.hidden = this.profile.selectionMode !== 'continuous-band';
    this.renderGroups(this.modelGroups, this.profile.modelGroups || []);
    this.renderGroups(this.electricalGroups, this.profile.electricalGroups || []);

    const specStrip = this.profile.specStrip || {};
    this.setScopeText('[data-pfs-flow-switch-top-spec="flow"]', specStrip.flow);
    this.setScopeText('[data-pfs-flow-switch-top-spec="pipe"]', specStrip.pipe);
    this.setScopeText('[data-pfs-flow-switch-top-spec="material"]', specStrip.material);
    this.setScopeText('[data-pfs-flow-switch-top-sku]', this.copy.skuPending || 'Complete configuration');
    this.setScopeText('[data-pfs-flow-switch-top-price]', this.copy.pricePending || 'Price shown after configuration');
    const topCompare = this.productScope?.querySelector('[data-pfs-flow-switch-top-compare]');
    if (topCompare) topCompare.hidden = true;
  }

  bindEvents() {
    const signal = this.controller.signal;

    this.addEventListener('click', (event) => {
      const pipeButton = event.target.closest('[data-pfs-flow-switch-pipe]');
      const dutyButton = event.target.closest('[data-pfs-flow-switch-duty]');
      const modelButton = event.target.closest('[data-pfs-flow-switch-model]');
      if (!pipeButton && !dutyButton && !modelButton) return;

      this.resetRemoteResolution();
      this.withTransition(() => {
        if (pipeButton) {
          this.state.pipe = pipeButton.dataset.value || null;
          this.state.targetFlow = null;
          this.state.dutyId = null;
          if (this.flowInput) this.flowInput.value = '';
        }
        if (dutyButton) this.state.dutyId = dutyButton.dataset.value || null;
        if (modelButton) this.state.model = modelButton.dataset.value || null;
        this.render();
      });

      if (pipeButton && this.profile.selectionMode === 'continuous-band') this.flowInput?.focus();
    }, { signal });

    this.flowInput?.addEventListener('input', () => {
      this.resetRemoteResolution();
      const value = this.flowInput.value.trim();
      this.state.targetFlow = value === '' ? null : Number(value);
      this.state.dutyId = null;
      this.render();
    }, { signal });

    this.addEventListener('change', (event) => {
      const select = event.target.closest('[data-pfs-flow-switch-selection]');
      if (!select) return;
      this.resetRemoteResolution();
      this.withTransition(() => {
        if (select.value) this.state.selections[select.dataset.key] = select.value;
        else delete this.state.selections[select.dataset.key];
        this.render();
      });
    }, { signal });

    this.retryButton?.addEventListener('click', () => {
      this.resetRemoteResolution();
      this.render();
    }, { signal });

    this.form?.addEventListener('submit', (event) => {
      if (this.variantInput?.value) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.formStatus) this.formStatus.textContent = this.copy.completeStatus || 'Complete the configuration before adding this switch.';
    }, { capture: true, signal });
  }

  withTransition(update) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !reduceMotion) {
      const transition = document.startViewTransition(update);
      transition.finished.catch(() => {});
    } else update();
  }

  selectionHeading() {
    if (this.profile.selectionMode === 'fixed-on-off') return 'Published ON/OFF point';
    if (this.profile.selectionMode === 'discrete-range') return 'Published operating range';
    return 'Matched sensing element';
  }

  selectionHelp() {
    if (this.profile.selectionMode === 'fixed-on-off') return 'Choose an exact published pair. Values between pairs are not rounded or interpolated.';
    if (this.profile.selectionMode === 'discrete-range') return 'Choose one published range to derive the internal sensing code.';
    return 'The target flow can match one or more published sensing assemblies.';
  }

  renderChoiceButtons(container, options, type) {
    if (!container) return;
    container.replaceChildren();
    options.forEach((option) => {
      const button = document.createElement('button');
      const label = document.createElement('strong');
      button.type = 'button';
      button.dataset[`pfsFlowSwitch${type[0].toUpperCase()}${type.slice(1)}`] = '';
      button.dataset.value = option.value;
      button.setAttribute('aria-pressed', 'false');
      label.textContent = option.label;
      button.append(label);
      if (option.detail) {
        const detail = document.createElement('small');
        detail.textContent = option.detail;
        button.append(detail);
      }
      container.append(button);
    });
  }

  renderGroups(container, groups) {
    if (!container) return;
    container.replaceChildren();
    groups.forEach((group) => {
      const field = document.createElement('label');
      const label = document.createElement('span');
      const select = document.createElement('select');
      field.className = 'pfs-flow-switch__select-field';
      field.dataset.groupKey = group.key;
      label.className = 'pfs-flow-switch__label';
      label.textContent = group.label;
      select.dataset.pfsFlowSwitchSelection = '';
      select.dataset.key = group.key;
      field.append(label, select);
      container.append(field);
    });
  }

  renderGroupOptions(row) {
    getProfileGroups(this.profile).forEach((group) => {
      const select = this.querySelector(`[data-pfs-flow-switch-selection][data-key="${group.key}"]`);
      if (!select) return;
      const allowed = getAllowedOptions(group, row);
      select.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = group.placeholder || `Choose ${group.label.toLowerCase()}`;
      select.append(placeholder);
      allowed.forEach((option) => {
        const node = document.createElement('option');
        node.value = option.value;
        node.textContent = option.label;
        select.append(node);
      });
      select.value = this.state.selections[group.key] || '';
    });
  }

  renderDutyChoices(candidates) {
    if (!this.dutyChoices) return;
    const focusedDuty = this.dutyChoices.contains(document.activeElement)
      ? document.activeElement.closest('[data-pfs-flow-switch-duty]')
      : null;
    const focusedDutyId = focusedDuty?.dataset.value || null;
    this.dutyChoices.replaceChildren();
    candidates.forEach((row) => {
      const button = document.createElement('button');
      const title = document.createElement('strong');
      const detail = document.createElement('small');
      button.type = 'button';
      button.className = 'pfs-flow-switch__match';
      button.dataset.pfsFlowSwitchDuty = '';
      button.dataset.value = row.id;
      button.setAttribute('aria-pressed', String(this.state.dutyId === row.id));
      if (this.profile.selectionMode === 'fixed-on-off') {
        title.textContent = `ON ${row.on} GPM`;
        detail.textContent = `OFF ${row.off} GPM · ${formatSensing(this.profile, row)}`;
      } else {
        title.textContent = `${row.min}–${row.max} GPM`;
        detail.textContent = formatSensing(this.profile, row);
      }
      button.append(title, detail);
      this.dutyChoices.append(button);
    });
    if (focusedDutyId) {
      const nextFocusedDuty = Array.from(this.dutyChoices.querySelectorAll('[data-pfs-flow-switch-duty]'))
        .find((button) => button.dataset.value === focusedDutyId);
      nextFocusedDuty?.focus({ preventScroll: true });
    }
  }

  render() {
    const candidates = getDutyCandidates(this.profile, this.state);
    if (this.profile.selectionMode === 'continuous-band') {
      if (candidates.length === 1) this.state.dutyId = candidates[0].id;
      else if (!candidates.some((row) => row.id === this.state.dutyId)) this.state.dutyId = null;
    } else if (!candidates.some((row) => row.id === this.state.dutyId)) {
      this.state.dutyId = null;
    }

    let row = getResolvedDuty(this.profile, this.state);
    this.state.selections = normalizeSelections(this.profile, this.state, row);
    row = getResolvedDuty(this.profile, this.state);

    this.renderGroupOptions(row);
    this.renderDutyChoices(candidates);
    this.renderStatus(candidates, row);
    this.renderSelectionState(row);
    this.renderSummary(row);
    this.renderPurchase(row);
  }

  renderStatus(candidates, row) {
    const pipeLabel = getPipeLabel(this.profile, this.state.pipe);
    const hasTarget = Number.isFinite(Number(this.state.targetFlow)) && this.state.targetFlow !== null;
    let state = 'pending';
    let message = 'Choose a pipe size to continue.';

    if (this.state.pipe && this.profile.selectionMode === 'continuous-band' && !hasTarget) {
      message = 'Enter the target switch point in GPM.';
    } else if (this.state.pipe && this.profile.selectionMode === 'continuous-band' && candidates.length === 0) {
      state = 'error';
      message = `No published band covers ${this.state.targetFlow} GPM in a ${pipeLabel} pipe.`;
    } else if (this.profile.selectionMode === 'continuous-band' && candidates.length > 1 && !row) {
      state = 'choice';
      message = `${candidates.length} published builds cover ${this.state.targetFlow} GPM. Choose the intended band.`;
    } else if (row) {
      state = 'valid';
      message = `${formatDuty(this.profile, row, this.state.targetFlow)} resolves to ${formatSensing(this.profile, row)}.`;
    } else if (this.state.pipe && this.profile.selectionMode === 'fixed-on-off') {
      message = 'Choose one exact nominal ON/OFF pair.';
    } else if (this.state.pipe) {
      message = 'Choose one published operating range.';
    }

    if (this.dutyStatus) {
      this.dutyStatus.dataset.state = state;
      this.dutyStatus.textContent = message;
    }

    const rows = getDutyRows(this.profile, this.state.pipe);
    if (this.bandsElement) {
      if (!rows.length) this.bandsElement.textContent = 'Choose a pipe size to see published choices.';
      else if (this.profile.selectionMode === 'continuous-band') this.bandsElement.textContent = `Published bands: ${rows.map((item) => `${item.min}–${item.max}`).join(' · ')} GPM`;
      else this.bandsElement.textContent = this.profile.omittedRowsNote || this.profile.catalogNote || `${rows.length} published choices for ${pipeLabel}.`;
    }
  }

  renderSelectionState(row) {
    const pipeLabel = getPipeLabel(this.profile, this.state.pipe);
    const target = Number.isFinite(Number(this.state.targetFlow)) && this.state.targetFlow !== null ? `${this.state.targetFlow} GPM` : '';
    const model = this.profile.models.find((option) => option.value === this.state.model);
    const electricalLabels = this.profile.electricalGroups.map((group) => {
      const selected = getAllowedOptions(group, row).find((option) => option.value === this.state.selections[group.key]);
      return selected?.label || '';
    }).filter(Boolean);

    this.querySelectorAll('[data-pfs-flow-switch-pipe]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.value === this.state.pipe)));
    this.querySelectorAll('[data-pfs-flow-switch-model]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.value === this.state.model)));
    this.querySelectorAll('[data-pfs-flow-switch-duty]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.value === this.state.dutyId)));

    if (this.flowInput) {
      this.flowInput.disabled = !this.state.pipe;
      const rows = getDutyRows(this.profile, this.state.pipe);
      this.flowInput.min = rows.length ? String(Math.min(...rows.map((item) => item.min))) : '';
      this.flowInput.max = rows.length ? String(Math.max(...rows.map((item) => item.max))) : '';
    }

    this.setText('[data-pfs-flow-switch-duty-current]', [pipeLabel, target].filter(Boolean).join(' · ') || '—');
    this.setText('[data-pfs-flow-switch-sensing-current]', formatSensing(this.profile, row) || '—');
    this.setText('[data-pfs-flow-switch-model-current]', model?.label || '—');
    this.setText('[data-pfs-flow-switch-electrical-current]', electricalLabels.join(' · ') || '—');

    const modelComplete = Boolean(this.state.model) && (this.profile.modelGroups || []).every((group) => this.state.selections[group.key]);
    const electricalComplete = this.profile.electricalGroups.every((group) => this.state.selections[group.key]);
    const railComplete = [Boolean(this.state.pipe && (this.profile.selectionMode !== 'continuous-band' || target)), Boolean(row), modelComplete, electricalComplete];
    this.querySelectorAll('[data-pfs-flow-switch-rail]').forEach((item, index) => {
      item.dataset.complete = String(railComplete[index]);
    });
  }

  renderSummary(row) {
    const model = this.profile.models.find((option) => option.value === this.state.model);
    const codeParts = this.profile.codeParts?.(this.state, row) || [];
    if (this.codeElement) {
      this.codeElement.replaceChildren();
      this.codeElement.style.setProperty('--pfs-flow-code-parts', String(Math.max(codeParts.length, 1)));
      codeParts.forEach((part) => {
        const segment = document.createElement('span');
        const value = document.createElement('b');
        const label = document.createElement('small');
        value.textContent = part.value || '—';
        label.textContent = part.label;
        segment.append(value, label);
        this.codeElement.append(segment);
      });
    }

    const groupRows = getProfileGroups(this.profile).map((group) => {
      const option = getAllowedOptions(group, row).find((candidate) => candidate.value === this.state.selections[group.key]);
      return [group.label, option?.label || '—'];
    });
    const summaryRows = [
      ['Pipe size', getPipeLabel(this.profile, this.state.pipe) || '—'],
      ['Operating duty', formatDuty(this.profile, row, this.state.targetFlow) || '—'],
      ['Sensing element', formatSensing(this.profile, row) || '—'],
      ['Model', model?.label || '—'],
      ...groupRows,
      ['Body material', model?.material || '—'],
      ['Maximum temperature', model?.temperature || '—'],
      ['Price', '—'],
    ];
    this.renderDefinitionList(summaryRows);
  }

  renderDefinitionList(rows) {
    if (!this.summaryDetails) return;
    this.summaryDetails.replaceChildren();
    rows.forEach(([term, description]) => {
      const wrapper = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = term;
      dd.textContent = description;
      if (term === 'Price') dd.dataset.pfsFlowSwitchSummaryPrice = '';
      wrapper.append(dt, dd);
      this.summaryDetails.append(wrapper);
    });
  }

  renderPurchase(row) {
    const expectedSku = buildExpectedSku(this.profile, this.state, row);
    const complete = isConfigurationComplete(this.profile, this.state, row);
    const localVariant = findExactVariant(this.variants, expectedSku);
    this.prepareRemoteVariant(expectedSku, row, localVariant);
    const remoteVariant = this.remoteKey === expectedSku && this.remoteStatus === 'resolved' ? this.remoteVariant : null;
    const variant = localVariant || remoteVariant;
    const canAdd = complete && isVariantPurchasable(variant);
    const remotePending = Boolean(complete && !localVariant && this.remoteKey === expectedSku && this.remoteStatus === 'pending');
    const remoteError = Boolean(complete && !localVariant && this.remoteKey === expectedSku && this.remoteStatus === 'error');
    const missing = Boolean(complete && expectedSku && !variant && !remotePending && !remoteError);
    const unavailable = Boolean(variant && !variant.available);
    const price = variant ? variant.formattedPrice || this.formatMoney(variant.price) : '—';

    this.setText('[data-pfs-flow-switch-mark]', canAdd ? 'Catalog match' : remotePending ? 'Checking catalog' : remoteError ? 'Catalog check failed' : unavailable ? 'Unavailable' : 'Configuration pending');
    const note = complete && variant?.sku
      ? `${this.copy.skuLabel || 'SKU'}: ${variant.sku}${unavailable ? ' · Currently unavailable' : ''}`
      : remotePending
        ? 'Checking this exact configuration against the catalog.'
      : remoteError
        ? 'The catalog check failed. Retry without changing your configuration.'
      : missing
        ? this.copy.catalogMissing || 'This published configuration is not present in the current catalog.'
        : unavailable
          ? this.copy.configurationUnavailable || 'This exact configuration is currently unavailable.'
          : this.copy.summaryPending || 'Complete all four stations to resolve the exact catalog SKU and price.';
    const noteElement = this.querySelector('[data-pfs-flow-switch-note]');
    if (noteElement) {
      noteElement.textContent = note;
      noteElement.dataset.state = complete && variant?.sku ? 'resolved' : 'pending';
    }
    this.setText('[data-pfs-flow-switch-summary-price]', price);
    this.setScopeText('[data-pfs-flow-switch-top-sku]', variant?.sku || this.copy.skuPending || 'Complete configuration');
    this.setScopeText('[data-pfs-flow-switch-top-price]', variant ? price : this.copy.pricePending || 'Price shown after configuration');

    const topCompare = this.productScope?.querySelector('[data-pfs-flow-switch-top-compare]');
    if (topCompare) {
      const hasCompare = Boolean(variant?.formattedCompareAtPrice) || Boolean(variant && Number(variant.compare_at_price) > Number(variant.price));
      topCompare.hidden = !hasCompare;
      topCompare.textContent = hasCompare ? variant.formattedCompareAtPrice || this.formatMoney(variant.compare_at_price) : '';
    }

    if (this.formStatus) {
      this.formStatus.textContent = canAdd
        ? 'Ready to add this exact configuration.'
        : remotePending
          ? 'Checking this exact configuration against the catalog.'
        : remoteError
          ? 'The catalog check failed. Retry without changing your configuration.'
        : missing
          ? 'This documented configuration is missing from the current catalog.'
          : unavailable
            ? 'This exact configuration is currently unavailable.'
            : this.copy.completeStatus || 'Complete the configuration before adding this switch.';
    }
    if (this.variantInput) this.variantInput.value = canAdd ? String(variant.id) : '';
    if (this.addButton) this.addButton.disabled = !canAdd;
    if (this.retryButton) this.retryButton.hidden = !remoteError;

    this.updateQuantityRule(variant);
    this.updateCartProperties(row);
    this.updateVariantUrl(canAdd ? variant : null);
  }

  prepareRemoteVariant(expectedSku, row, localVariant) {
    if (!expectedSku || localVariant || !this.profile.optionValueLabels) {
      this.resetRemoteResolution();
      return;
    }
    if (this.remoteKey === expectedSku) return;

    this.catalogController?.abort();
    this.remoteVariant = null;
    this.remoteKey = expectedSku;
    this.remoteStatus = 'pending';
    this.resolveRemoteVariant(expectedSku, row);
  }

  async resolveRemoteVariant(expectedSku, row) {
    const labels = this.profile.optionValueLabels(this.state, row);
    const optionValueIds = labels.map((label) => this.catalogOptionIds.get(label)).filter(Boolean);
    if (optionValueIds.length !== labels.length) {
      if (this.remoteKey === expectedSku) {
        this.remoteStatus = 'missing';
        this.render();
      }
      return;
    }

    const controller = new AbortController();
    this.catalogController = controller;
    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.delete('variant');
    requestUrl.searchParams.set('section_id', this.productScope?.dataset.sectionId || '');
    requestUrl.searchParams.set('option_values', optionValueIds.join(','));

    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Catalog request failed with status ${response.status}`);
      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextProduct = responseDocument.querySelector('[data-pfs-product]');
      const nextFallback = nextProduct?.querySelector('[data-pfs-standard-purchase]');
      const variantId = nextFallback?.querySelector('input[name="id"]')?.value || '';
      const sku = nextProduct?.querySelector('[data-pfs-flow-switch-top-sku]')?.textContent?.trim() || '';
      const formattedPrice = nextProduct?.querySelector('[data-pfs-flow-switch-top-price]')?.textContent?.trim() || '';
      const formattedCompareAtPrice = nextProduct?.querySelector('[data-pfs-flow-switch-top-compare]:not([hidden])')?.textContent?.trim() || '';
      const purchaseButton = nextFallback?.querySelector('[data-pfs-add]');
      const quantityInput = nextFallback?.querySelector('input[name="quantity"]');

      if (this.remoteKey !== expectedSku || controller.signal.aborted) return;
      if (!variantId || sku !== expectedSku) {
        this.remoteStatus = 'missing';
        this.remoteVariant = null;
      } else {
        this.remoteStatus = 'resolved';
        this.remoteVariant = {
          id: variantId,
          sku,
          available: Boolean(purchaseButton && !purchaseButton.disabled),
          formattedPrice,
          formattedCompareAtPrice,
          quantity_rule: {
            min: quantityInput?.min || 1,
            max: quantityInput?.max || null,
            increment: quantityInput?.step || 1,
          },
        };
      }
      this.render();
    } catch (error) {
      if (error.name === 'AbortError' || this.remoteKey !== expectedSku) return;
      this.remoteStatus = 'error';
      this.remoteVariant = null;
      this.render();
    }
  }

  resetRemoteResolution() {
    this.catalogController?.abort();
    this.catalogController = null;
    this.remoteVariant = null;
    this.remoteKey = null;
    this.remoteStatus = 'idle';
    if (this.variantInput) this.variantInput.value = '';
    if (this.addButton) this.addButton.disabled = true;
  }

  updateQuantityRule(variant) {
    if (!this.quantityInput) return;
    const rule = variant?.quantity_rule || {};
    const minimum = Number(rule.min) || 1;
    const increment = Number(rule.increment) || 1;
    this.quantityInput.min = String(minimum);
    this.quantityInput.step = String(increment);
    if (rule.max) this.quantityInput.max = String(rule.max);
    else this.quantityInput.removeAttribute('max');
    if (Number(this.quantityInput.value) < minimum) this.quantityInput.value = String(minimum);
  }

  updateCartProperties(row) {
    const model = this.profile.models.find((option) => option.value === this.state.model);
    const electrical = this.profile.electricalGroups.map((group) => {
      const option = getAllowedOptions(group, row).find((candidate) => candidate.value === this.state.selections[group.key]);
      return option ? `${group.label}: ${option.label}` : '';
    }).filter(Boolean).join(' · ');
    const values = {
      profile: this.profile.family,
      pipe: getPipeLabel(this.profile, this.state.pipe),
      duty: formatDuty(this.profile, row, this.state.targetFlow),
      sensing: formatSensing(this.profile, row),
      model: model?.label || '',
      electrical,
    };
    Object.entries(values).forEach(([key, value]) => {
      const input = this.querySelector(`[data-pfs-flow-switch-property="${key}"]`);
      if (input) input.value = value;
    });
  }

  updateVariantUrl(variant) {
    const url = new URL(window.location.href);
    if (variant) url.searchParams.set('variant', String(variant.id));
    else url.searchParams.delete('variant');
    window.history.replaceState({}, '', url);
  }

  formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '—';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(value / 100);
  }

  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value || '—';
  }

  setScopeText(selector, value) {
    const element = this.productScope?.querySelector(selector);
    if (element) element.textContent = value || '—';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('pfs-flow-switch-builder')) {
  customElements.define('pfs-flow-switch-builder', PfsFlowSwitchBuilder);
}
